import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { autocompleteClasses } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import styled from "@mui/material/styles/styled";

import { vestResolver } from "@hookform/resolvers/vest";
import { format } from "date-fns";
import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import { useAppDispatch } from "app/hooks";
import DateStringFormField from "components/date-string-form-field";
import InputFormField from "components/input-form-field";
import { TagTypes } from "constants/tags";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import CreatedByBlock from "features/scheduling/scheduler/appointment-preview/created-by-block.component";
import { useUpdateAssignedTagMutation } from "services/assigned-tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import {
  convertDateInTimezoneToCurrent,
  formatBackendDateOnly,
  formatDateToDateAndTime,
  isDateStringValid
} from "utils/common";

import {
  setCustomMessage,
  setOpen
} from "../action-snackbar/action-snackbar-slice";
import { tagChipFormValidator } from "./tag-chip-form-validator";

type TagChipFormProps = {
  assignedTag: AssignedTag;
  open: boolean;
  anchorEl: any;
  onClose: () => void;
  tagType: TagTypes;
  disableUpdateMutation?: boolean;
  onUpdate?: (tag: AssignedTag) => void;
};

const StyledPopover = styled(Popover)(() => ({
  [`& .${autocompleteClasses.paper}`]: {
    marginTop: "9px"
  },
  // Above the raised dialogs that host tags (the staff task modal sits at snackbar + 2).
  zIndex: 1403
}));

const paperSx = {
  px: 1.5,
  py: 1,
  border: "solid 1px #ccc",
  overflow: "visible",
  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
  "& .MuiAvatar-root": {
    width: 32,
    height: 32,
    ml: -0.5,
    mr: 1
  }
};

export interface TagChipFormValues {
  description: string;
  expires_at: string;
}

const EMPTY_DEFAULT_VALUES = {
  description: "",
  expires_at: ""
};

export default function TagChipForm({
  assignedTag,
  open,
  anchorEl,
  onClose,
  tagType,
  disableUpdateMutation,
  onUpdate
}: TagChipFormProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const { id, tag, object_id, description, expires_at, created_by } =
    assignedTag;

  const defaultValues: TagChipFormValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_VALUES, {
      description,
      expires_at:
        expires_at && isDateStringValid(expires_at)
          ? format(new Date(expires_at), "MM/dd/yyyy")
          : ""
    });
  }, [description, expires_at]);

  const formMethods = useForm<TagChipFormValues>({
    mode: "onChange",
    resolver: vestResolver(tagChipFormValidator(), undefined, {
      mode: "sync"
    }),
    defaultValues
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid }
  } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [updateAssignedTag] = useUpdateAssignedTagMutation();

  const onSubmit = (data: TagChipFormValues) => {
    if (disableUpdateMutation) {
      onUpdate?.({ ...assignedTag, ...data, updated_by: currentUser as any });
      onClose();
    } else {
      updateAssignedTag({
        id: id,
        object_id,
        tag_type: tagType,
        description: data.description,
        expires_at: data.expires_at
          ? formatBackendDateOnly(data.expires_at)
          : null
      }).then((response) => {
        if ("data" in response) {
          onUpdate?.({
            ...response.data,
            tag,
            created_by,
            updated_by: currentUser as any
          });
          dispatch(setCustomMessage("Tag updated"));
          dispatch(setOpen(1500));
          onClose();
        }
      });
    }
  };

  const currentClinic = useAppSelector(selectCurrentClinic);
  const timezone = currentClinic?.timezone;
  const creator = assignedTag?.created_by
    ? `${assignedTag?.created_by?.first_name} ${assignedTag?.created_by?.last_name}`.trim()
    : "";
  const createdDate =
    assignedTag?.created_at &&
    formatDateToDateAndTime(
      new Date(convertDateInTimezoneToCurrent(assignedTag.created_at, timezone))
    );

  const updatedBy = assignedTag?.updated_by
    ? `${assignedTag?.updated_by?.first_name} ${assignedTag?.updated_by?.last_name}`.trim()
    : "";
  const lastUpdateDate =
    assignedTag?.updated_at &&
    formatDateToDateAndTime(
      new Date(
        convertDateInTimezoneToCurrent(assignedTag?.updated_at, timezone)
      )
    );

  return (
    <StyledPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      slotProps={paperProps}
    >
      <Box
        sx={{ minWidth: 300 }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack spacing={3} sx={{ pt: 1, pb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                borderRadius: "50%",
                width: 18,
                height: 18,
                background: tag.color
              }}
            ></Box>
            <Box>{tag.name}</Box>
          </Stack>
          <CreatedByBlock
            creator={creator}
            createdDate={createdDate}
            updatedBy={updatedBy}
            lastUpdateDate={lastUpdateDate}
            containerSx={{
              mt: 0,
              mb: 0,
              height: "auto"
            }}
            labelSx={{
              fontSize: "11px",
              width: "auto",
              minWidth: "67px"
            }}
            valueSx={{
              fontSize: "11px",
              width: "auto",
              lineHeight: 1.4,
              WebkitLineClamp: "3"
            }}
          />
          <DateStringFormField
            name="expires_at"
            label="Tag End Date"
            control={control}
            sx={{
              ml: 2,
              mt: 1
            }}
            PopperProps={{ style: { zIndex: 1404 } }}
          />
          <InputFormField
            name="description"
            label="Description"
            control={control}
            multiline={true}
          />
          <Stack
            spacing={2}
            sx={{ mt: 2 }}
            direction="row"
            justifyContent="space-between"
          >
            <Button size="small" variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="small"
              type="submit"
              variant="contained"
              disabled={!isValid}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </StyledPopover>
  );
}

const paperProps = {
  paper: {
    elevation: 0,
    sx: paperSx
  }
};

const anchorOrigin = {
  vertical: "bottom",
  horizontal: "left"
} as const;
